import { useMemo, useState } from 'react'
import {
  IconArrowDownRight, IconArrowUpRight, IconChevronRight, IconFileText, IconFolder, IconNotebook, IconPlus,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'

import { homeApi } from '@/api/endpoints'
import type { HomeFeedItem, HomeSummary } from '@/api/types'
import { ActivityChart } from '@/components/home/ActivityChart'
import { PageHeader } from '@/components/common/PageHeader'
import { RoleBadge } from '@/components/common/RoleBadge'
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States'
import { SectionCard, StatTile } from '@/components/common/Surface'
import { CreateProjectModal } from '@/pages/ProjectsPage'
import { useAuthStore } from '@/store/authStore'
import { formatBytes, formatRelative } from '@/utils/format'
import { tr } from '@/i18n/tr'
import { Avatar, Button, ButtonLink, FileTile, Monogram, Segmented, TextLink, useDisclosure } from '@/ui'
import { projectColor } from '../../../packages/shared/src/tokens'
import './home.css'

/**
 * Ana sayfa: giriş yapan kullanıcının açılış ekranı.
 *
 * <p>Tüm veri tek istekten gelir ({@code GET /api/home}); panel başına ayrı
 * istek atılsaydı sayfa beş parça halinde, farklı anlarda dolardı.
 */
export function HomePage() {
  const user = useAuthStore((s) => s.user)
  const [yeniProje, { open, close }] = useDisclosure(false)

  const query = useQuery({ queryKey: ['home'], queryFn: homeApi.summary })

  const ad = user?.displayName.split(/\s+/)[0] ?? ''
  const plus = <IconPlus size={16} stroke={2.2} aria-hidden="true" />

  return (
    <>
      <PageHeader
        eyebrow={dayjs().format('D MMMM, dddd')}
        title={`${selam()}, ${ad}`}
        subtitle={query.data ? altBaslik(query.data) : undefined}
        actions={
          <>
            <ButtonLink variant="secondary" to="/projeler">Tüm projeler</ButtonLink>
            <Button leftIcon={plus} onClick={open}>{tr.project.newProject}</Button>
          </>
        }
      />

      {query.isLoading && <LoadingState />}
      {query.isError && <ErrorState error={query.error} onRetry={() => query.refetch()} />}

      {query.data && query.data.stats.projects === 0 && (
        <EmptyState
          icon={<IconFolder size={22} stroke={1.6} />}
          title={tr.project.empty}
          hint={tr.project.emptyHint}
          action={<Button leftIcon={plus} onClick={open}>{tr.project.newProject}</Button>}
        />
      )}

      {query.data && query.data.stats.projects > 0 && <Pano data={query.data} />}

      <CreateProjectModal opened={yeniProje} onClose={close} />
    </>
  )
}

type Aralik = '7' | '14' | '30'

function Pano({ data }: { data: HomeSummary }) {
  const s = data.stats
  const [aralik, setAralik] = useState<Aralik>('14')
  const gunler = useMemo(() => data.daily.slice(-Number(aralik)), [data.daily, aralik])
  const notToplam = gunler.reduce((a, d) => a + d.notes, 0)
  const dosyaToplam = gunler.reduce((a, d) => a + d.files, 0)
  const zirve = gunler.reduce((a, d) => (d.notes + d.files > a.notes + a.files ? d : a), gunler[0])
  const hafta = data.daily.slice(-7)
  const haftaMax = Math.max(1, ...hafta.map((d) => d.notes + d.files))
  const ic = { size: 17, stroke: 1.8 }

  return (
    <div className="nx-home">
      <div className="nx-home-stats">
        <StatTile label="Projeler" value={s.projects} icon={<IconFolder {...ic} />}
          hint={`${s.ownedProjects} sahibi olduğunuz · ${s.projects - s.ownedProjects} paylaşılan`} />
        <StatTile label="Notlar" value={s.notes} icon={<IconNotebook {...ic} />} hint={`bu hafta ${s.notesThisWeek} yeni`} />
        <StatTile label="Dosyalar" value={s.files} icon={<IconFileText {...ic} />}
          hint={`${formatBytes(s.fileBytes)} · ${s.fileVersions.toLocaleString('tr-TR')} sürüm`} />
        <StatTile dark label="Son 7 gün" value={s.changesThisWeek}
          icon={<Degisim simdi={s.changesThisWeek} once={s.changesPreviousWeek} />}
          aside={
            <span className="nx-spark" aria-hidden="true">
              {hafta.map((d, i) => (
                <span key={d.date} data-today={i === hafta.length - 1 ? '' : undefined}
                  style={{ height: Math.max(3, Math.round(((d.notes + d.files) / haftaMax) * 40)) }} />
              ))}
            </span>
          }
          hint={degisimMetni(s.changesThisWeek, s.changesPreviousWeek)} />
      </div>

      <div className="nx-home-grid">
        <SectionCard id="h-act" className="nx-home-wide" title="Günlük hareket" description="Projelerinizdeki not ve dosya değişiklikleri"
          actions={
            <Segmented aria-label="Zaman aralığı" value={aralik} onChange={setAralik}
              options={[{ value: '7', label: '7 gün' }, { value: '14', label: '14 gün' }, { value: '30', label: '30 gün' }]} />
          }>
          <div className="nx-home-summary">
            <Ozet deger={notToplam + dosyaToplam} etiket="toplam değişiklik" />
            <Ozet deger={notToplam} etiket="not" renk="var(--nx-chart-note)" />
            <Ozet deger={dosyaToplam} etiket="dosya" renk="var(--nx-lime)" />
            {notToplam + dosyaToplam > 0 && <Ozet deger={dayjs(zirve.date).format('D MMM')} etiket="en yoğun gün" />}
          </div>
          {notToplam + dosyaToplam > 0 ? (
            <ActivityChart days={gunler} />
          ) : (
            <Bos title={`Son ${aralik} günde değişiklik yok`} hint="Projelerinizde not ya da dosya düzenlendiğinde burada görünür." />
          )}
        </SectionCard>

        <SectionCard id="h-top" className="nx-home-narrow" title="En aktif projeler" description="Son 7 gün"
          actions={<TextLink to="/projeler" className="nx-home-all">Tümü</TextLink>}>
          {data.activeProjects.length === 0 ? (
            <Bos title="Bu hafta hareket yok" hint="Projelerinizde son 7 günde değişiklik olmadı." />
          ) : (
            <AktifProjeler projeler={data.activeProjects} />
          )}
        </SectionCard>

        <SectionCard id="h-feed" className="nx-home-wide" title="Son hareketler" description="Projelerinizde başkalarının yaptıkları">
          {data.feed.length === 0 ? (
            <Bos title="Henüz başkasından hareket yok" hint="Projelerinizi paylaştığınız kişilerin işlemleri burada listelenir." />
          ) : (
            <Akis ogeler={data.feed} />
          )}
        </SectionCard>

        <SectionCard id="h-cont" className="nx-home-narrow" title="Kaldığınız yerden" description="En son düzenledikleriniz">
          {data.recent.length === 0 ? (
            <Bos title="Henüz düzenleme yok" hint="Düzenlediğiniz notlar ve dosyalar burada görünür." />
          ) : (
            <div className="nx-home-resume">
              {data.recent.map((r) => (
                <Link key={`${r.type}-${r.id}`} className="nx-list-item nx-resume"
                  to={r.type === 'NOTE' ? `/projeler/${r.projectId}/notlar/${r.id}` : `/projeler/${r.projectId}/dosyalar`}>
                  {r.type === 'NOTE'
                    ? <span className="nx-resume__note" aria-hidden="true">NOT</span>
                    : <FileTile kind={r.fileKind ?? undefined} name={r.name} size={38} radius={10} />}
                  <span className="nx-resume__text">
                    <span className="nx-resume__name">{r.name}</span>
                    <ProjeEtiketi id={r.projectId} ad={r.projectName} ek={formatRelative(r.at)} />
                  </span>
                  <IconChevronRight size={18} stroke={1.8} aria-hidden="true" className="nx-resume__chev" />
                </Link>
              ))}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  )
}

// --- parçalar ---------------------------------------------------------

function Ozet({ deger, etiket, renk }: { deger: React.ReactNode; etiket: string; renk?: string }) {
  return (
    <div className="nx-home-ozet">
      <span className="nx-home-ozet__label">
        {renk && <span className="nx-home-ozet__dot" style={{ background: renk }} aria-hidden="true" />}{etiket}
      </span>
      <span className="nx-home-ozet__value nx-nums">{deger}</span>
    </div>
  )
}

/**
 * Geçen haftaya göre değişim rozeti.
 *
 * <p>Düşüş kırmızı DEĞİL: daha az düzenleme kötü bir şey değil, yalnızca
 * sakin bir hafta. Renk burada bir yargı taşımamalı.
 */
function Degisim({ simdi, once }: { simdi: number; once: number }) {
  if (once === 0) return null
  const oran = Math.round(((simdi - once) / once) * 100)
  if (oran === 0) return null
  const artis = oran > 0
  return (
    <span className={artis ? 'nx-trend nx-trend--up' : 'nx-trend'}>
      {artis ? <IconArrowUpRight size={13} stroke={2.4} /> : <IconArrowDownRight size={13} stroke={2.4} />}
      <span className="nx-nums">%{Math.abs(oran)}</span>
    </span>
  )
}

function degisimMetni(simdi: number, once: number) {
  if (once === 0) return simdi > 0 ? 'geçen hafta hareket yoktu' : 'geçen hafta da sakindi'
  return simdi === once ? 'geçen haftayla aynı' : 'geçen haftaya göre değişiklik'
}

function AktifProjeler({ projeler }: { projeler: HomeSummary['activeProjects'] }) {
  const enCok = Math.max(...projeler.map((p) => p.changes))
  return (
    <div className="nx-home-list">
      {projeler.map((p) => {
        const renk = projectColor(p.id)
        return (
          <Link key={p.id} to={`/projeler/${p.id}`} className="nx-list-item">
            <Monogram name={p.name} color={renk} size={38} radius={11} />
            <span className="nx-active__main">
              <span className="nx-active__title">
                <span className="nx-active__name">{p.name}</span>
                <RoleBadge role={p.myRole} size="xs" />
              </span>
              <span className="nx-active__bar"><span style={{ width: `${(p.changes / enCok) * 100}%`, background: renk }} /></span>
            </span>
            <span className="nx-active__count">
              <strong className="nx-nums">{p.changes}</strong>
              <span>değişiklik</span>
            </span>
          </Link>
        )
      })}
    </div>
  )
}

function Akis({ ogeler }: { ogeler: HomeFeedItem[] }) {
  const gruplar: Array<{ gun: string; ogeler: HomeFeedItem[] }> = []
  for (const e of ogeler) {
    const gun = gunEtiketi(e.at)
    const son = gruplar[gruplar.length - 1]
    if (son && son.gun === gun) son.ogeler.push(e)
    else gruplar.push({ gun, ogeler: [e] })
  }
  return (
    <div className="nx-feed">
      {gruplar.map((g) => (
        <div key={g.gun} className="nx-feed__day">
          <span className="nx-feed__title">{g.gun}</span>
          {g.ogeler.map((e, i) => {
            const aktor = e.actorName ?? 'Silinmiş kullanıcı'
            return (
              <Link key={`${e.at}-${i}`} to={akisBaglantisi(e)} className="nx-list-item nx-feed__item">
                <Avatar name={aktor} size={34} />
                <span className="nx-feed__main">
                  <span className="nx-feed__text"><b>{aktor}</b>{akisCumlesiBasi(e)}{akisCumlesi(e)}</span>
                  <ProjeEtiketi id={e.projectId} ad={e.projectName} />
                </span>
                <span className="nx-feed__time nx-nums">
                  {g.gun === 'Bugün' ? formatRelative(e.at) : dayjs(e.at).format('HH:mm')}
                </span>
              </Link>
            )
          })}
        </div>
      ))}
    </div>
  )
}

/** Panel içi boş durum. EmptyState kendi çerçevesini çizdiği için panelde iç içe kutu olurdu. */
function Bos({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="nx-home-empty">
      <strong>{title}</strong>
      <span>{hint}</span>
    </div>
  )
}

function ProjeEtiketi({ id, ad, ek }: { id: string; ad: string; ek?: string }) {
  return (
    <span className="nx-proj-tag">
      <span className="nx-proj-tag__dot" style={{ background: projectColor(id) }} aria-hidden="true" />
      <span className="nx-proj-tag__name">{ad}{ek && <> · {ek}</>}</span>
    </span>
  )
}

// --- metin ------------------------------------------------------------

function selam() {
  const saat = new Date().getHours()
  if (saat >= 5 && saat < 12) return 'Günaydın'
  if (saat >= 12 && saat < 18) return 'İyi günler'
  if (saat >= 18 && saat < 23) return 'İyi akşamlar'
  return 'İyi geceler'
}

function altBaslik(d: HomeSummary): React.ReactNode {
  if (!d.previousLoginAt) return 'Arsivora’ya hoş geldiniz.'
  if (d.changesSinceLastLogin === 0) return 'Son girişinizden bu yana projelerinizde yeni bir değişiklik yok.'
  return (
    <>
      Son girişinizden bu yana projelerinizde{' '}
      <strong className="nx-home-mark nx-nums">{d.changesSinceLastLogin} değişiklik</strong>{' '}
      oldu.
    </>
  )
}

function gunEtiketi(iso: string) {
  const d = dayjs(iso)
  if (d.isSame(dayjs(), 'day')) return 'Bugün'
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Dün'
  return d.format('D MMMM')
}

/** Hedef silinmişse kullanıcı projeye düşer; ölü bir bağlantı 404 verirdi. */
function akisBaglantisi(e: HomeFeedItem) {
  const proje = `/projeler/${e.projectId}`
  if (e.targetType === 'NOTE' && e.targetAvailable) return `${proje}/notlar/${e.targetId}`
  if (e.targetType === 'FILE' && e.targetAvailable) return `${proje}/dosyalar`
  if (e.action.startsWith('MEMBER_') || e.action.startsWith('PROJECT_TEAM_')
    || e.action === 'OWNERSHIP_TRANSFERRED') return `${proje}/uyeler`
  return proje
}

/** Adla başlamayan cümleler ("sizi projeye ekledi") için aktörden sonra boşluk. */
function akisCumlesiBasi(e: HomeFeedItem) {
  const adlaBaslar = e.action.startsWith('NOTE_') || e.action.startsWith('FILE_')
    || e.action.startsWith('PROJECT_TEAM_')
    || (e.action === 'MEMBER_ADDED' || e.action === 'MEMBER_ROLE_CHANGED' || e.action === 'MEMBER_REMOVED')
      && !e.subjectIsMe
  return adlaBaslar ? '' : ' '
}

function akisCumlesi(e: HomeFeedItem): React.ReactNode {
  // Cümle kalın bir adla başlıyorsa aktörden virgülle ayrılır; yoksa iki ad yapışık okunur.
  const hedef = <>, <b>{e.targetName ?? 'adsız'}</b></>
  const kisi = <>, <b>{e.subjectName ?? 'bir üyeyi'}</b></>
  switch (e.action) {
    case 'NOTE_CREATED': return <>{hedef} notunu oluşturdu</>
    case 'NOTE_EDITED': return <>{hedef} notunu düzenledi</>
    case 'NOTE_DELETED': return <>{hedef} notunu sildi</>
    case 'NOTE_RESTORED': return <>{hedef} notunu geri yükledi</>
    case 'NOTE_VERSION_RESTORED': return <>{hedef} notunun eski bir sürümüne döndü</>
    case 'FILE_UPLOADED': return <>{hedef} dosyasını yükledi</>
    case 'FILE_VERSION_CREATED': return <>{hedef} dosyasının yeni sürümünü kaydetti</>
    case 'FILE_DELETED': return <>{hedef} dosyasını sildi</>
    case 'FILE_RESTORED': return <>{hedef} dosyasını geri yükledi</>
    case 'FILE_VERSION_RESTORED': return <>{hedef} dosyasının eski bir sürümüne döndü</>
    case 'MEMBER_ADDED':
      return e.subjectIsMe ? <>sizi projeye ekledi</> : <>{kisi} kişisini projeye ekledi</>
    case 'MEMBER_ROLE_CHANGED':
      return e.subjectIsMe ? <>projedeki rolünüzü değiştirdi</> : <>{kisi} kişisinin rolünü değiştirdi</>
    case 'MEMBER_REMOVED': return <>{kisi} kişisini projeden çıkardı</>
    case 'OWNERSHIP_TRANSFERRED': return <>proje sahipliğini devretti</>
    case 'PROJECT_TEAM_ADDED': return <>{hedef} ekibini projeye ekledi</>
    case 'PROJECT_TEAM_ROLE_CHANGED': return <>{hedef} ekibinin rolünü değiştirdi</>
    case 'PROJECT_TEAM_REMOVED': return <>{hedef} ekibini projeden kaldırdı</>
    case 'PROJECT_VISIBILITY_CHANGED': return <>projenin görünürlüğünü değiştirdi</>
    case 'PROJECT_UPDATED': return <>proje bilgilerini güncelledi</>
    default: return <>bir işlem yaptı</>
  }
}
